import * as React from "react";
import { ChangeEvent } from "react";
import TreatmentOption from "./TreatmentOption";
import Treatment from "../commons/model/treatment/Treatment";
import TreatmentSelection from "../commons/model/treatment/TreatmentSelection";
import ModalDelete from "../commons/components/modal/ModalDelete";
import { byAssignmentPosition } from "../commons/utils/treatment";

interface TreatmentOptionSelectorProps {
  selection: TreatmentSelection;
  onChange: (newSelection: TreatmentSelection) => any;
  categoryId: string;
}

interface TreatmentOptionSelectorState {
  oldTreatmentBeingRemoved?: Treatment;
}

export default class TreatmentOptionSelector extends React.Component<
  TreatmentOptionSelectorProps,
  TreatmentOptionSelectorState
> {
  constructor(props: TreatmentOptionSelectorProps) {
    super(props);
    this.state = {};
    this.removeOption = this.removeOption.bind(this);
    this.requestRemoveOption = this.requestRemoveOption.bind(this);
  }

  render() {
    const selection = this.props.selection;
    const sortedSelectedTreatments = selection.selectedTreatments.sort((t1, t2) =>
      byAssignmentPosition(this.props.categoryId, t1, t2)
    );
    const allTreatments: { [key: string]: Treatment } = [
      ...sortedSelectedTreatments,
      ...selection.availableTreatments,
    ]
      .sort((t1, t2) => byAssignmentPosition(this.props.categoryId, t1, t2))
      .reduce((acc, t) => ((acc[t.uid] = t), acc), {});

    return (
      <div
        id={selection.uid}
        className="treatment-option-selector vertical-dropdown h-flex align-center group"
      >
        <label>{i18n["Page.AssessmentResult.Treatment.Selector.Label"]}</label>
        <div className="h-flex vertical-dropdown-toggle full-width full-height align-center space-between">
          {sortedSelectedTreatments[0] && (
            <TreatmentOption
              treatment={sortedSelectedTreatments[0]}
              onRemove={this.requestRemoveOption}
            />
          )}
          {sortedSelectedTreatments.length > 1 && (
            <div className="h-flex align-center space-between total">
              <div>...</div>
              <div className="label primary">
                <span>{sortedSelectedTreatments.length}</span>
              </div>
            </div>
          )}
          {!sortedSelectedTreatments.length && <span className="label spacer">&nbsp;</span>}
        </div>
        <div className="options container-vertical-dropdown">
          {Object.values(allTreatments).map((treatment) => (
            <label
              key={treatment.uid}
              htmlFor={`${selection.uid}_${treatment.uid}`}
              className={
                "option clickable h-flex align-center" +
                (sortedSelectedTreatments.includes(treatment) ? " selected" : "")
              }
            >
              <input
                type="checkbox"
                onChange={(e) => this.toggleOption(treatment, e)}
                id={`${selection.uid}_${treatment.uid}`}
                checked={sortedSelectedTreatments.includes(treatment)}
              />
              <span>
                {treatment.name +
                  (this.isOldTreatment(treatment)
                    ? i18n["Page.AssessmentResult.Treatment.Selector.OldTreatment"]
                    : "")}
              </span>
            </label>
          ))}
        </div>
        <i className="ic_chevron_down" />
        {this.state.oldTreatmentBeingRemoved && (
          <ModalDelete
            title={i18n["Page.AssessmentResult.Treatment.Warning.OldTreatmentWillBeRemoved.Title"]}
            actionButtonLabel={i18n["Global.Remove"]}
            cancel={() => this.setState({ oldTreatmentBeingRemoved: null })}
            action={() => {
              const treatment = this.state.oldTreatmentBeingRemoved;
              this.setState({ oldTreatmentBeingRemoved: null }, () => this.removeOption(treatment));
              return Promise.resolve();
            }}
          >
            {i18n["Page.AssessmentResult.Treatment.Warning.OldTreatmentWillBeRemoved.Message"]}
          </ModalDelete>
        )}
      </div>
    );
  }

  componentDidMount() {
    window["verticalDropdownUtil"] && window["verticalDropdownUtil"].reAttach();
  }

  componentDidUpdate() {
    window["verticalDropdownUtil"] && window["verticalDropdownUtil"].reAttach();
  }

  private isOldTreatment(treatment: Treatment): boolean {
    return this.props.selection.availableTreatments.indexOf(treatment) === -1;
  }

  private addOption(treatment: Treatment) {
    this.props.onChange({
      ...this.props.selection,
      selectedTreatments: [...this.props.selection.selectedTreatments, treatment],
    });
  }

  private requestRemoveOption(treatment: Treatment) {
    if (this.isOldTreatment(treatment)) {
      this.setState({ oldTreatmentBeingRemoved: treatment });
    } else {
      this.removeOption(treatment);
    }
  }

  private removeOption(treatment: Treatment) {
    const newSelectedTreatments = [...this.props.selection.selectedTreatments];
    newSelectedTreatments.splice(this.props.selection.selectedTreatments.indexOf(treatment), 1);
    this.props.onChange({
      ...this.props.selection,
      selectedTreatments: newSelectedTreatments,
    });
  }

  private toggleOption(treatment: Treatment, e: ChangeEvent) {
    if (e.target["checked"]) {
      this.addOption(treatment);
    } else {
      this.requestRemoveOption(treatment);
    }
  }
}
